import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare, X, Paperclip, Send, Sparkles, Download, FileText,
  Image as ImageIcon, ArrowLeft, Hash, User as UserIcon, Search, Pencil, Trash2, Plus,
} from "lucide-react";
import TaskDialog from "@/components/TaskDialog";
import { fmtDateTime } from "@/utils/date";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25MB per file
const MAX_TOTAL_BYTES = 30 * 1024 * 1024; // total per message

function humanBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

async function fileToDataUrl(file) {
  return await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function linkify(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+|@[A-Za-z][\w\-]{1,40})/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p)) return <a key={i} href={p} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 underline break-all">{p}</a>;
    if (/^@[A-Za-z]/.test(p)) return <span key={i} className="text-[#E11D2E] font-medium">{p}</span>;
    return <span key={i}>{p}</span>;
  });
}

const REACTION_EMOJIS = ["👍", "❤️", "🎉", "😂", "🔥", "✅"];

/**
 * Detect an in-progress @mention at the cursor.
 * Returns { query, start } or null.
 * A mention is triggered when the caret is right after `@word` and word is [A-Za-z0-9_ -]{0,40}
 * The `@` must be at the very start of the string or preceded by whitespace.
 */
function detectMentionQuery(value, caret) {
  if (!value) return null;
  const upto = value.slice(0, caret);
  // Search back for the last @
  const at = upto.lastIndexOf("@");
  if (at === -1) return null;
  // '@' must be start-of-string or preceded by whitespace
  if (at > 0 && !/\s/.test(upto.charAt(at - 1))) return null;
  const query = upto.slice(at + 1);
  // Do not fire if there is whitespace inside the query
  if (/\s/.test(query)) return null;
  // Cap the length to avoid weirdness
  if (query.length > 40) return null;
  return { query, start: at };
}

function MentionSuggest({ users, query, activeIdx, onPick, onHover, anchor = "top" }) {
  if (!users.length) return null;
  return (
    <div
      data-testid="chat-mention-suggest"
      className={`absolute left-3 right-3 ${anchor === "top" ? "bottom-full mb-2" : "top-full mt-2"} bg-surface border border-white/10 rounded-lg shadow-2xl max-h-56 overflow-y-auto scrollbar-thin z-50`}
    >
      <div className="px-3 py-1.5 text-[0.7rem] uppercase tracking-widest text-white/40 border-b border-white/5">
        Mention {query ? `· “${query}”` : ""}
      </div>
      {users.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onPick(u); }}
          onMouseEnter={() => onHover?.(i)}
          data-testid={`chat-mention-item-${u.id}`}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${i === activeIdx ? "bg-white/10" : "hover:bg-white/5"}`}
        >
          <Avatar user={u} size={22} />
          <div className="min-w-0 flex-1">
            <div className="text-xs truncate">{u.name}</div>
            <div className="text-[0.7rem] text-white/40 truncate">{u.title || u.role} · {u.team}</div>
          </div>
          <span className="text-[0.7rem] text-[#E11D2E]">@{u.name.split(" ")[0]}</span>
        </button>
      ))}
    </div>
  );
}

function Attachment({ att, onDownload }) {
  const isImg = (att.type || "").startsWith("image/");
  if (isImg && att.data_url) {
    return (
      <a href={att.data_url} download={att.name} target="_blank" rel="noreferrer" className="block max-w-[240px]">
        <img src={att.data_url} alt={att.name} className="rounded-md max-h-56 border border-white/10" />
        <div className="text-[0.7rem] text-white/40 mt-1 flex items-center gap-1"><ImageIcon size={10} /> {att.name} · {humanBytes(att.size)}</div>
      </a>
    );
  }
  return (
    <a href={att.data_url} download={att.name}
       className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-2 max-w-[280px]">
      <FileText size={16} className="text-white/60 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs truncate">{att.name}</div>
        <div className="text-[0.7rem] text-white/40">{humanBytes(att.size)} · original quality</div>
      </div>
      <Download size={13} className="text-white/40" />
    </a>
  );
}

function MessageBubble({ msg, currentUserId, isSuper, onDetectTask, onEdit, onDelete, onReact }) {
  const mine = msg.sender_id === currentUserId;
  const canEdit = mine || isSuper;
  const [hover, setHover] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  if (msg.deleted) {
    return (
      <div className={`flex gap-2 items-start ${mine ? "flex-row-reverse" : ""}`}>
        <Avatar user={{ name: msg.sender_name, avatar_url: msg.sender_avatar }} size={28} />
        <div className="text-xs italic text-white/30 rounded-lg px-3 py-2 border border-dashed border-white/8">Message deleted</div>
      </div>
    );
  }
  const reactions = msg.reactions || {};
  const reactionList = Object.entries(reactions).filter(([, users]) => users && users.length);
  return (
    <div
      className={`group flex gap-2 items-start ${mine ? "flex-row-reverse" : ""}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPickerOpen(false); }}
    >
      <Avatar user={{ name: msg.sender_name, avatar_url: msg.sender_avatar }} size={28} />
      <div className={`flex-1 min-w-0 ${mine ? "text-right" : ""}`}>
        <div className="flex items-center gap-2 mb-0.5 text-[0.7rem] text-white/40 font-mono">
          <span className={mine ? "order-2" : ""}>{msg.sender_name}</span>
          <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {msg.edited_at && <span className="italic">(edited)</span>}
        </div>
        <div className={`inline-block max-w-full text-left rounded-lg px-3 py-2 text-sm ${
          mine ? "bg-[#E11D2E]/20 border border-[#E11D2E]/30" : "bg-white/5 border border-white/8"
        }`}>
          {msg.text && <div className="whitespace-pre-wrap break-words">{linkify(msg.text)}</div>}
          {msg.attachments?.length > 0 && (
            <div className={`space-y-2 ${msg.text ? "mt-2" : ""}`}>
              {msg.attachments.map((a, i) => <Attachment key={a.url || `${a.name}-${i}`} att={a} />)}
            </div>
          )}
        </div>
        {reactionList.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : ""}`}>
            {reactionList.map(([emoji, users]) => {
              const mineReacted = users.includes(currentUserId);
              return (
                <button
                  key={emoji}
                  onClick={() => onReact(msg, emoji)}
                  data-testid={`chat-reaction-${msg.id}-${emoji}`}
                  className={`text-xs px-2 py-0.5 rounded-full border transition ${mineReacted ? "bg-[#E11D2E]/20 border-[#E11D2E]/40" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
                >
                  <span className="mr-1">{emoji}</span>
                  <span className="text-white/60">{users.length}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className={`flex items-center gap-2 mt-1 ${mine ? "justify-end" : ""}`}>
          {onDetectTask && msg.text?.length > 8 && (
            <button
              onClick={() => onDetectTask(msg)}
              data-testid={`chat-detect-task-${msg.id}`}
              className="text-[0.7rem] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
            >
              <Sparkles size={10} /> AI: turn into task
            </button>
          )}
          {hover && (
            <div className="relative">
              <button onClick={() => setPickerOpen((v) => !v)} data-testid={`chat-react-btn-${msg.id}`}
                className="text-[0.7rem] text-white/40 hover:text-white">😊 +</button>
              {pickerOpen && (
                <div className="absolute bottom-5 left-0 z-10 flex gap-0.5 p-1 rounded-full bg-surface border border-white/10 shadow-lg" data-testid={`chat-react-picker-${msg.id}`}>
                  {REACTION_EMOJIS.map((e) => (
                    <button key={e} onClick={() => { onReact(msg, e); setPickerOpen(false); }}
                      className="w-7 h-7 rounded-full hover:bg-white/10 text-base">{e}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {canEdit && hover && (
            <>
              {mine && (
                <button onClick={() => onEdit(msg)} className="text-[0.7rem] text-white/40 hover:text-white inline-flex items-center gap-1" data-testid={`chat-edit-${msg.id}`}>
                  <Pencil size={10} /> Edit
                </button>
              )}
              <button onClick={() => onDelete(msg)} className="text-[0.7rem] text-white/40 hover:text-red-400 inline-flex items-center gap-1" data-testid={`chat-delete-${msg.id}`}>
                <Trash2 size={10} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ChatDrawer({ open, onClose }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]); // {name, size, type, data_url}
  const [sending, setSending] = useState(false);
  const [detecting, setDetecting] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [users, setUsers] = useState([]);
  const [dmPickerOpen, setDmPickerOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState("");
  // @mention autocomplete state
  const [mentionCtx, setMentionCtx] = useState(null); // { query, start, target: "composer" | "search" }
  const [mentionIdx, setMentionIdx] = useState(0);
  const composerRef = useRef(null);
  const searchInputRef = useRef(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const isSuper = !!user?.is_super_admin;

  // Users matching the current @query (first-name-first match, cap 6)
  const mentionMatches = useMemo(() => {
    if (!mentionCtx) return [];
    const q = mentionCtx.query.toLowerCase();
    return users
      .filter((u) => u.id !== user?.id)
      .filter((u) => {
        if (!q) return true;
        const n = u.name.toLowerCase();
        return n.startsWith(q) || n.split(" ").some((p) => p.startsWith(q));
      })
      .slice(0, 6);
  }, [users, mentionCtx, user?.id]);

  // Reset highlighted item whenever the query changes
  useEffect(() => { setMentionIdx(0); }, [mentionCtx?.query, mentionCtx?.target]);

  const loadRooms = async () => {
    try {
      const { data } = await api.get("/chat/rooms");
      setRooms(data.rooms || []);
      // Only auto-open when there's exactly one room
      if (!activeRoom && data.rooms?.length === 1) setActiveRoom(data.rooms[0]);
    } catch { /* silent */ }
  };

  const loadMessages = async (room) => {
    try {
      const { data } = await api.get(`/chat/rooms/${encodeURIComponent(room.room_id)}/messages`);
      setMessages(data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 40);
    } catch (e) {
      toast.error("Failed to load messages");
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch { /* silent */ }
  };

  useEffect(() => { if (open) { loadRooms(); loadUsers(); } }, [open]); // eslint-disable-line
  useEffect(() => { if (activeRoom) loadMessages(activeRoom); }, [activeRoom]); // eslint-disable-line
  // Poll active room every 8s while open
  useEffect(() => {
    if (!open || !activeRoom) return undefined;
    const i = setInterval(() => loadMessages(activeRoom), 8000);
    return () => clearInterval(i);
  }, [open, activeRoom]); // eslint-disable-line

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > MAX_FILE_BYTES) { toast.error(`"${f.name}" is larger than 25MB`); continue; }
      const total = pending.reduce((s, p) => s + p.size, 0) + f.size;
      if (total > MAX_TOTAL_BYTES) { toast.error("Total attachments exceed 30MB"); break; }
      const data_url = await fileToDataUrl(f);
      setPending((p) => [...p, { name: f.name, size: f.size, type: f.type, data_url }]);
    }
    e.target.value = "";
  };

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    if (!activeRoom) return;
    setSending(true);
    try {
      await api.post(`/chat/rooms/${encodeURIComponent(activeRoom.room_id)}/messages`,
        { text: text.trim(), attachments: pending });
      setText(""); setPending([]);
      await loadMessages(activeRoom);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  const detectTask = async (msg) => {
    setDetecting(msg.id);
    try {
      const { data } = await api.post("/chat/detect-task", { text: msg.text });
      if (!data.is_task) {
        toast.info(`AI: this doesn't look like a task (${Math.round((data.confidence || 0) * 100)}% confidence)`);
        return;
      }
      let assigned = null;
      if (data.assignee_hint) {
        const hint = data.assignee_hint.toLowerCase();
        assigned = users.find((u) => u.name.toLowerCase().includes(hint) || hint.includes(u.name.toLowerCase()));
      }
      setPrefill({
        title: data.title || msg.text.slice(0, 60),
        description: data.description || `From chat: ${msg.text}`.slice(0, 500),
        client: data.client || "",
        deadline: data.deadline || "",
        assigned_to: assigned?.id || "",
        priority: false,
        nas_link: "",
      });
      setTaskOpen(true);
    } catch (e) {
      toast.error("AI detection failed");
    } finally { setDetecting(null); }
  };

  const startDM = async (userId) => {
    try {
      const { data } = await api.post(`/chat/dm/${userId}`);
      setDmPickerOpen(false);
      await loadRooms();
      // Find the newly created/existing room and open it
      setActiveRoom({ room_id: data.room_id, kind: "dm", name: data.name, peer_id: userId });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start DM");
    }
  };

  const editMessage = (msg) => {
    setEditingMsg(msg);
    setEditText(msg.text);
  };

  const submitEdit = async () => {
    if (!editingMsg) return;
    try {
      await api.patch(`/chat/messages/${editingMsg.id}`, { text: editText });
      toast.success("Edited");
      setEditingMsg(null); setEditText("");
      if (activeRoom) loadMessages(activeRoom);
    } catch (e) {
      toast.error("Edit failed");
    }
  };

  const deleteMessage = async (msg) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/chat/messages/${msg.id}`);
      if (activeRoom) loadMessages(activeRoom);
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const runSearch = async () => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults(null); return; }
    try {
      const { data } = await api.get("/chat/search", { params: { q } });
      setSearchResults(data.results || []);
    } catch { setSearchResults([]); }
  };

  const reactTo = async (msg, emoji) => {
    try {
      await api.post(`/chat/messages/${msg.id}/react`, { emoji });
      if (activeRoom) loadMessages(activeRoom);
    } catch { toast.error("Reaction failed"); }
  };

  // ------- @mention autocomplete helpers -------
  const onComposerChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart ?? val.length;
    setText(val);
    const ctx = detectMentionQuery(val, caret);
    setMentionCtx(ctx ? { ...ctx, target: "composer" } : null);
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart ?? val.length;
    setSearchQ(val);
    const ctx = detectMentionQuery(val, caret);
    setMentionCtx(ctx ? { ...ctx, target: "search" } : null);
    // Also clear server search results when actively typing an @mention
    if (ctx) setSearchResults(null);
  };

  const insertMentionIntoComposer = (u) => {
    if (!mentionCtx) return;
    const firstName = u.name.split(" ")[0].replace(/\W+/g, "");
    const before = text.slice(0, mentionCtx.start);
    // find current caret via composer input
    const caret = composerRef.current?.selectionStart ?? text.length;
    const after = text.slice(caret);
    const insert = `@${firstName} `;
    const next = before + insert + after;
    setText(next);
    setMentionCtx(null);
    // Restore focus + caret right after inserted mention
    requestAnimationFrame(() => {
      if (composerRef.current) {
        const pos = (before + insert).length;
        composerRef.current.focus();
        composerRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const pickMention = (u) => {
    if (!mentionCtx) return;
    if (mentionCtx.target === "composer") {
      insertMentionIntoComposer(u);
    } else {
      // From search: open (or create) a DM with that user
      setSearchQ("");
      setMentionCtx(null);
      startDM(u.id);
    }
  };

  const handleMentionKeys = (e) => {
    if (!mentionCtx || mentionMatches.length === 0) return false;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionIdx((i) => (i + 1) % mentionMatches.length);
      return true;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
      return true;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pickMention(mentionMatches[mentionIdx]);
      return true;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setMentionCtx(null);
      return true;
    }
    return false;
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px] lg:bg-transparent" onClick={onClose} />
      <div
        className="fixed left-0 top-0 h-full w-full sm:w-[380px] z-50 bg-surface border-r border-white/10 shadow-2xl animate-fade-in-up flex flex-col"
        data-testid="chat-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-12 border-b border-white/5">
          <div className="flex items-center gap-2">
            {activeRoom && (
              <button onClick={() => { setActiveRoom(null); setSearchResults(null); }} className="p-1 hover:bg-white/5 rounded">
                <ArrowLeft size={14} />
              </button>
            )}
            <MessageSquare size={14} className="text-[#E11D2E]" />
            <div className="text-sm font-display tracking-tight">
              {activeRoom ? activeRoom.name : "Team Chat"}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!activeRoom && (
              <button onClick={() => setDmPickerOpen(true)} data-testid="chat-new-dm-btn"
                title="Start a direct message"
                className="p-1 hover:bg-white/5 rounded text-white/60">
                <Plus size={16} />
              </button>
            )}
            <button onClick={onClose} data-testid="chat-close-btn" className="p-1 hover:bg-white/5 rounded">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Search bar (only in rooms list) */}
        {!activeRoom && (
          <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2 relative">
            <Search size={13} className="text-white/40" />
            <Input
              ref={searchInputRef}
              value={searchQ}
              onChange={onSearchChange}
              onKeyDown={(e) => {
                if (handleMentionKeys(e)) return;
                if (e.key === "Enter") runSearch();
              }}
              onBlur={() => setTimeout(() => { if (mentionCtx?.target === "search") setMentionCtx(null); }, 120)}
              placeholder="Search chats… try @name for people"
              data-testid="chat-search-input"
              className="bg-transparent border-white/10 h-8 text-xs focus-visible:ring-red-500"
            />
            {searchQ && (
              <button onClick={() => { setSearchQ(""); setSearchResults(null); setMentionCtx(null); }} className="text-white/40 hover:text-white">
                <X size={12} />
              </button>
            )}
            {mentionCtx?.target === "search" && mentionMatches.length > 0 && (
              <MentionSuggest
                users={mentionMatches}
                query={mentionCtx.query}
                activeIdx={mentionIdx}
                onPick={pickMention}
                onHover={setMentionIdx}
                anchor="bottom"
              />
            )}
          </div>
        )}

        {/* Rooms list OR messages */}
        {!activeRoom ? (
          searchResults ? (
            <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-2">
              <div className="text-[0.7rem] uppercase tracking-widest text-white/40 mb-2">Search · {searchResults.length} result{searchResults.length === 1 ? "" : "s"}</div>
              {searchResults.length === 0 && <div className="text-white/40 text-xs">No matches.</div>}
              {searchResults.map((m) => (
                <button key={m.id}
                  onClick={() => {
                    const r = rooms.find((rr) => rr.room_id === m.room_id);
                    if (r) { setActiveRoom(r); setSearchResults(null); setSearchQ(""); }
                  }}
                  className="w-full text-left p-2 rounded-md border border-white/8 bg-black/20 hover:bg-white/5">
                  <div className="text-[0.7rem] text-white/40 font-mono mb-1">
                    {m.sender_name} · {fmtDateTime(m.created_at)}
                  </div>
                  <div className="text-xs text-white/80 break-words line-clamp-3">{m.text}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-1">
              {rooms.length === 0 && (
                <div className="text-white/40 text-xs p-4 text-center">Loading rooms…</div>
              )}
              {rooms.map((r) => (
                <button
                  key={r.room_id}
                  onClick={() => setActiveRoom(r)}
                  data-testid={`chat-room-${r.team || r.peer_id}`}
                  className="w-full text-left p-3 rounded-md hover:bg-white/5 transition-colors flex items-start gap-2"
                >
                  {r.kind === "dm" ? (
                    <Avatar user={{ name: r.name, avatar_url: r.peer_avatar }} size={22} />
                  ) : (
                    <Hash size={14} className="text-white/40 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate flex items-center gap-2">
                      {r.name}
                      {r.unread > 0 && <span className="text-[0.7rem] bg-[#E11D2E] text-white rounded-full px-1.5 py-0.5">{r.unread}</span>}
                    </div>
                    <div className="text-xs text-white/40 truncate mt-0.5">{r.last_message || "No messages yet."}</div>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3">
              {messages.length === 0 ? (
                <div className="text-center text-white/30 text-xs py-8">Say hi to your team 👋</div>
              ) : (
                messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} currentUserId={user?.id} isSuper={isSuper}
                    onDetectTask={detecting === m.id ? null : detectTask}
                    onEdit={editMessage} onDelete={deleteMessage} onReact={reactTo} />
                ))
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-white/5 p-3 relative">
              {mentionCtx?.target === "composer" && mentionMatches.length > 0 && (
                <MentionSuggest
                  users={mentionMatches}
                  query={mentionCtx.query}
                  activeIdx={mentionIdx}
                  onPick={pickMention}
                  onHover={setMentionIdx}
                  anchor="top"
                />
              )}
              {pending.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2 max-h-24 overflow-y-auto scrollbar-thin">
                  {pending.map((p, i) => (
                    <div key={`${p.name}-${p.size}-${i}`} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs">
                      {p.type.startsWith("image/") ? <ImageIcon size={11} /> : <FileText size={11} />}
                      <span className="max-w-[140px] truncate">{p.name}</span>
                      <span className="text-white/40">{humanBytes(p.size)}</span>
                      <button onClick={() => setPending(pending.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  onChange={onFile}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="chat-attach-btn"
                  className="p-2 rounded-md hover:bg-white/5 text-white/60"
                  title="Attach file (original quality)"
                >
                  <Paperclip size={16} />
                </button>
                <Input
                  ref={composerRef}
                  value={text}
                  onChange={onComposerChange}
                  onKeyDown={(e) => {
                    if (handleMentionKeys(e)) return;
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                  }}
                  onBlur={() => setTimeout(() => { if (mentionCtx?.target === "composer") setMentionCtx(null); }, 120)}
                  placeholder="Type a message… @name to tag a teammate"
                  data-testid="chat-input"
                  className="flex-1 bg-black/40 border-white/10 focus-visible:ring-red-500"
                />
                <Button
                  onClick={send}
                  disabled={sending}
                  data-testid="chat-send-btn"
                  className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full h-9 w-9 p-0"
                >
                  <Send size={14} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        users={users}
        existing={prefill ? { ...prefill, id: null } : null}
        onSaved={() => { setTaskOpen(false); setPrefill(null); }}
      />

      {/* New DM picker */}
      <Dialog open={dmPickerOpen} onOpenChange={setDmPickerOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Start a direct message</DialogTitle>
            <DialogDescription className="text-white/50">
              Pick a teammate to chat with 1-on-1.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto scrollbar-thin space-y-1">
            {users.filter((u) => u.id !== user?.id).map((u) => (
              <button key={u.id}
                onClick={() => startDM(u.id)}
                data-testid={`chat-dm-user-${u.id}`}
                className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white/5 text-left">
                <Avatar user={u} size={30} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{u.name}</div>
                  <div className="text-[0.7rem] text-white/40 truncate">{u.email} · {u.team}</div>
                </div>
                <UserIcon size={13} className="text-white/40" />
              </button>
            ))}
            {users.length <= 1 && (
              <div className="text-white/40 text-xs p-4">No teammates available.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit message */}
      <Dialog open={!!editingMsg} onOpenChange={(o) => { if (!o) setEditingMsg(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Edit message</DialogTitle>
            <DialogDescription className="text-white/50">
              Your teammates will see an &ldquo;edited&rdquo; tag on the message.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            data-testid="chat-edit-input"
            className="w-full bg-black/40 border border-white/10 rounded-md p-3 text-sm min-h-[100px] focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditingMsg(null)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
            <button onClick={submitEdit} data-testid="chat-edit-submit" className="px-5 py-2 text-sm rounded-full bg-[#E11D2E] hover:bg-[#F03041] text-white">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
